import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Tabs,
    Tab,
    Button,
    CircularProgress,
    Alert,
    Chip,
    Stack,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Badge,
    IconButton,
    Tooltip,
    FormControlLabel,
    Checkbox,
    ToggleButtonGroup,
    ToggleButton,

} from '@mui/material';
import {
    Check,
    CloudQueue as CloudQueueIcon,
    FilterList as FilterListIcon,
    Schedule,
    CalendarMonth,
    ViewList,
    GridView,
    Warning,
    List,
} from '@mui/icons-material';
import { useAuth } from '../context/authContext';
import { useAppDispatch, useAppSelector } from '../redux/redux.hook';
import {
    fetchTimesheetsByUser,
    validateTimesheetByForeman,
    validateTimesheetByHR,
    updateTimesheet,
    syncPendingTimesheets,
    removeOfflineTimesheetOperation,
    editOfflineTimesheetOperation,
    fetchTimesheetsByForeman,
    fetchTimesheetsByHR
} from '../redux/timeSheet.slice';
import { fetchData } from '../redux/data.slice';
import { deleteTimesheet } from '../redux/timeSheet.slice';
import {
    getISOWeek, format, startOfWeek,
    addDays
} from 'date-fns';
import { fr } from 'date-fns/locale';
import {
    ArrowBackIos,
    ArrowForwardIos
} from '@mui/icons-material';
import {
    ButtonGroup,
} from '@mui/material';
import toast from 'react-hot-toast';
import { useUserRole } from '../context/userRoleContext';
import { Search, Clear } from '@mui/icons-material';
import { TimesheetEditDialog } from '../components/TimesheetEditDialog';
import TimesheetListItem from '../components/TimesheetListItem';
import TimesheetCard from '../components/TimesheetCard';
import { Timesheet } from '../interfaces/timesheet';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { makeRenderWeekDay } from '../components/datePicker/renderWeekDay';

// Tab type
type TabValue = 'all' | 'pending-sync' | 'validated' | 'not-validated';

// Main Timesheet List Component
export const TimesheetList: React.FC = () => {
    const [tabValue, setTabValue] = useState<TabValue>(() => {
        // Check if we have a stored tab preference
        const storedTab = sessionStorage.getItem('activeTimesheetTab');
        // Clear the stored preference to avoid unwanted redirects on future visits
        sessionStorage.removeItem('activeTimesheetTab');
        // Return the stored tab value if valid, otherwise 'all'
        return (storedTab as TabValue) || 'all';
    });

    const [hoveredDate, setHoveredDate] = useState<DateTime<true> | DateTime<false> | null>(null);

    // Redux state
    const { items, pendingItems, loading, error } = useAppSelector(state => state.timesheets);
    const { data, loading: dataLoading } = useAppSelector(state => state.data);
    const pendingUpdatesCount = pendingItems.filter(item => item._pendingUpdate && !item._pendingDelete).length;
    const pendingDeletesCount = pendingItems.filter(item => item._pendingDelete).length;
    const pendingNewCount = pendingItems.filter(item => !item._pendingUpdate && !item._pendingDelete).length;
    const totalCount = pendingUpdatesCount + pendingDeletesCount + pendingNewCount;
    const { isOnline } = useAppSelector(state => state.sync);

    const [editTimesheet, setEditTimesheet] = useState<Timesheet | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const { isAuthenticated } = useAuth();
    const { userRole, isAdmin } = useUserRole();
    const dispatch = useAppDispatch();
    const [viewType, setViewType] = useState<'card' | 'list'>('card');
    const [selectedTimesheets, setSelectedTimesheets] = useState<string[]>([]);
    const [selectAllChecked, setSelectAllChecked] = useState(false);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [datePickerOpen, setDatePickerOpen] = useState(false);

    // Combine server and local items with proper handling of pending operations
    const allTimesheets: Timesheet[] = React.useMemo(() => {
        // If server returns empty list, just return empty array
        if (!items || items.length === 0) {
            return [];
        }
        // Start with all server items
        let combinedItems = [...items];

        // Process pending items
        pendingItems.forEach(pendingItem => {
            if (pendingItem._pendingDelete && pendingItem.id) {
                // For items marked for deletion, remove them from the combined list
                combinedItems = combinedItems.filter(item =>
                    // Remove if IDs match for server items
                    !(item.id && pendingItem.id && item.id === pendingItem.id)
                );
            } else if (pendingItem._pendingUpdate && pendingItem.id) {
                // For items being updated, replace the server version with the pending version
                const existingIndex = combinedItems.findIndex(item =>
                    item.id && pendingItem.id && item.id === pendingItem.id
                );

                if (existingIndex !== -1) {
                    combinedItems[existingIndex] = pendingItem;
                } else {
                    combinedItems.push(pendingItem);
                }
            } else if (!pendingItem.id) {
                // For new items not yet synced to server, just add them
                combinedItems.push(pendingItem);
            }
        });

        return combinedItems;
    }, [items, pendingItems]);

    const filteredTimesheets: Timesheet[] = React.useMemo(() => {
        let filtered = [];
        // First filter by tab value
        switch (tabValue) {
            case 'pending-sync':
                filtered = pendingItems;
                break;
            case 'validated':
                filtered = userRole?.toLowerCase() === 'hr'
                    ? allTimesheets.filter(t => t.hr_validated)
                    : allTimesheets.filter(t => t.foreman_validated);
                break;
            case 'not-validated':
                filtered = userRole?.toLowerCase() === 'hr'
                    ? allTimesheets.filter(t => !t.hr_validated)
                    : allTimesheets.filter(t => !t.foreman_validated);
                break;
            case 'all':
            default:
                filtered = allTimesheets;
                break;
        }

        // Then apply search filter if there is a search term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(timesheet => {
                // Search by site name
                const siteMatch = timesheet.worksite?.name?.toLowerCase().includes(term);

                // Search by employee name
                const employeeMatch = timesheet.travailleur?.name?.toLowerCase().includes(term);

                // Search by employee name
                const foundEmployee = data?.employees?.find(emp =>
                    emp.matricule.toString().toLowerCase().includes(term) && timesheet.travailleur?.name?.toLowerCase().includes(emp.name.toLowerCase())
                );

                const employeeMatriculeMatch = foundEmployee ? foundEmployee.matricule.toString().toLowerCase().includes(term) : false;

                // Search by work manager/foreman name (only for HR, Admin, Foreman)
                const isPrivilegedUser = userRole && ['HR', 'Admin', 'Foreman'].includes(userRole);
                const workManagerMatch = isPrivilegedUser &&
                    timesheet.chefChantier?.name?.toLowerCase().includes(term);

                const foremanMatch = isPrivilegedUser &&
                    timesheet.chefProjet?.name?.toLowerCase().includes(term);

                return siteMatch || employeeMatch || workManagerMatch || foremanMatch || employeeMatriculeMatch;
            });
        }

        return filtered;
    }, [allTimesheets, pendingItems, tabValue, searchTerm, userRole, data?.employees]);

    // Count badges for tabs
    const validatedCount = userRole?.toLowerCase() === 'hr'
        ? allTimesheets.filter(t => t.hr_validated).length
        : allTimesheets.filter(t => t.foreman_validated).length;

    const notValidatedCount = userRole?.toLowerCase() === 'hr'
        ? allTimesheets.filter(t => !t.hr_validated).length
        : allTimesheets.filter(t => !t.foreman_validated).length;

    const [isRefreshing, setIsRefreshing] = useState(false);

    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [weekNumber, setWeekNumber] = useState<number>(getISOWeek(new Date()));
    const [isValidatingAll, setIsValidatingAll] = useState(false);
    const wasOfflineRef = useRef<boolean>(!isOnline);

    const fetch = React.useCallback(async () => {
        if (!isAuthenticated || !isOnline || !userRole) {
            return;
        }

        await dispatch(fetchData()).unwrap();

        if (!isAdmin) {
            if (userRole.toLocaleLowerCase() === "foreman") {
                await dispatch(fetchTimesheetsByForeman({
                    weekNumber: weekNumber,
                    year: selectedDate.getFullYear(),
                })).unwrap();
            } else if (userRole.toLocaleLowerCase() === "user") {
                await dispatch(fetchTimesheetsByUser({
                    weekNumber: weekNumber,
                    year: selectedDate.getFullYear()
                })).unwrap();
            }
        } else {
            await dispatch(fetchTimesheetsByHR({
                weekNumber: weekNumber,
                year: selectedDate.getFullYear(),
            })).unwrap();
        }
    }, [isAuthenticated, isOnline, userRole, isAdmin, weekNumber, selectedDate, dispatch]);

    // Load timesheets whenever user/session context changes.
    useEffect(() => {
        fetch()
    }, [fetch]);

    // Auto-sync pending offline changes when connection is restored.
    useEffect(() => {
        if (!isOnline) {
            wasOfflineRef.current = true;
            return;
        }

        const cameBackOnline = wasOfflineRef.current;
        wasOfflineRef.current = false;

        if (!cameBackOnline || !isAuthenticated || pendingItems.length === 0) {
            return;
        }

        const syncOnReconnect = async () => {
            try {
                await dispatch(syncPendingTimesheets()).unwrap();
                await fetch();
                toast.success('Données hors ligne synchronisées.');
            } catch (err) {
                console.error('Auto sync failed:', err);
                toast.error('Synchronisation automatique échouée. Réessayez manuellement.');
            }
        };

        syncOnReconnect();
    }, [isOnline, isAuthenticated, pendingItems.length, dispatch, fetch]);

    useEffect(() => {
        setSelectedTimesheets([]);
        setSelectAllChecked(false);
    }, [tabValue]);

    // Helper function
    const handleSelectTimesheet = (id: string) => {
        if (selectedTimesheets.includes(id)) {
            setSelectedTimesheets(selectedTimesheets.filter(itemId => itemId !== id));
        } else {
            setSelectedTimesheets([...selectedTimesheets, id]);
        }
    };

    // Handle "select all" functionality
    const handleSelectAll = (checked: boolean) => {
        setSelectAllChecked(checked);
        if (checked) {
            // Get all valid IDs from not validated timesheets that have an ID
            const allIds = filteredTimesheets
                .filter(t => {
                    // Check the appropriate validation field based on user role
                    if (userRole?.toLowerCase() === 'hr') {
                        return !t.hr_validated && t.id;
                    } else {
                        return !t.foreman_validated && t.id;
                    }
                })
                .map(t => t.id as string);
            setSelectedTimesheets(allIds);
        } else {
            setSelectedTimesheets([]);
        }
    };

    const calculateWeekNumber = (date: Date) => {
        return getISOWeek(date);
    };

    // Handle date change
    const handleDateChange = (date: Date | null) => {
        if (date) {
            const newWeekNumber = calculateWeekNumber(date);
            setSelectedDate(date);
            setWeekNumber(newWeekNumber);
        }
    };

    // Tab change handler
    const handleTabChange = (event: React.SyntheticEvent, newValue: TabValue) => {
        setTabValue(newValue);
    };

    const handleDeleteTimesheet = async (timesheet: Timesheet) => {
        if (window.confirm(`Êtes vous sûr de vouloir supprimer la timesheet de  ${timesheet.travailleur.name}?`)) {
            if (isOnline) {
                await dispatch(deleteTimesheet(timesheet)).unwrap();
                toast.success('Données enregistrées avec succès');

                // Refresh page data
                fetch();
            } else {
                await dispatch(removeOfflineTimesheetOperation({ ...timesheet, _pendingDelete: true, _pendingUpdate: false }));
                toast.success('Données sauvegardées localement (en mode hors ligne)');
            }
        }
    };

    // Validation handlers
    const handleValidateByForeman = async (id: string) => {
        if (!isOnline) {
            alert('Offline Mode: Cannot validate timesheets while offline.');
            return;
        }

        try {
            await dispatch(validateTimesheetByForeman(id)).unwrap();

            // Refresh page data
            fetch();
        } catch (err) {
            console.error('Error validating timesheet:', err);
        }
    };

    const handleValidateByHR = async (id: string) => {
        if (!isOnline) {
            alert('Offline Mode: Cannot validate timesheets while offline.');
            return;
        }

        try {
            await dispatch(validateTimesheetByHR(id)).unwrap();

            // Refresh page data
            fetch();
        } catch (err) {
            console.error('Error validating timesheet:', err);
        }
    };

    // Sync all handler
    const handleSyncAll = async () => {
        if (isRefreshing) return; // Prevent multiple clicks

        try {
            setIsRefreshing(true);

            await dispatch(syncPendingTimesheets()).unwrap();
            await fetch();

            // Set the tab for after refresh
            setTabValue('all');
            sessionStorage.setItem('activeTimesheetTab', 'all');
        } catch (error) {
            console.error('Sync failed:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    // Edit dialog handlers
    const handleOpenEditDialog = (timesheet: Timesheet) => {
        setEditTimesheet(timesheet);
        setDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setDialogOpen(false);
        setEditTimesheet(null);
    };

    const handleSaveTimesheet = async (updatedTimesheet: Timesheet) => {
        setIsRefreshing(true);

        if (isOnline) {
            await dispatch(updateTimesheet(updatedTimesheet)).unwrap();
            toast.success('Données enregistrées avec succès');

            // Refresh page data
            fetch();
        } else {
            await dispatch(editOfflineTimesheetOperation({ ...updatedTimesheet, _pendingUpdate: updatedTimesheet.localId ? false : true, _pendingDelete: false }));
            toast.success('Données sauvegardées localement (en mode hors ligne)');
        }

        setDialogOpen(false);
        setIsRefreshing(false);
    };

    const handleValidateSelectedTimesheets = async () => {
        if (!isOnline) {
            toast.error('Impossible de valider les timesheets en mode Hors ligne');
            return;
        }

        if (!userRole || !['foreman', 'hr'].includes(userRole.toLocaleLowerCase())) {
            toast.error('Vous n\'avez pas les droits pour valider cette timesheet');
            return;
        }

        const idsToValidate = selectedTimesheets.length > 0
            ? selectedTimesheets
            : filteredTimesheets
                .filter(t => !t.foreman_validated && t.id)
                .map(t => t.id as string);

        if (idsToValidate.length === 0) {
            toast.error('Aucun élément séléctionné pour validation');
            return;
        }

        try {
            setIsValidatingAll(true);
            let successCount = 0;
            let errorCount = 0;

            // Process each timesheet one by one
            for (const id of idsToValidate) {
                try {
                    if (userRole.toLowerCase() === 'Foreman'.toLowerCase()) {
                        await dispatch(validateTimesheetByForeman(id)).unwrap();
                    } else if (userRole.toLowerCase() === 'HR'.toLowerCase()) {
                        // Find the timesheet to check if it's already foreman validated
                        const timesheet = filteredTimesheets.find(t => t.id === id);
                        if (!timesheet?.hr_validated) {
                            await dispatch(validateTimesheetByHR(id)).unwrap();
                        }
                    }
                    successCount++;
                } catch (err) {
                    console.error('Erreur de la validation de la timesheet:', err);
                    errorCount++;
                }
            }

            // Show result toast
            if (successCount > 0) {
                toast.success(`${successCount} timesheets valideés avec succés`);
            }
            if (errorCount > 0) {
                toast.error(`${errorCount} ont échoués lors de la sychronisation`);
            }

            // Clear selections after validation
            setSelectedTimesheets([]);
            setSelectAllChecked(false);
        } catch (error) {
            console.error('Erreur de validation:', error);
            toast.error('Erreur de validation des timesheets');
        } finally {
            setIsValidatingAll(false);
            // Refresh the data after validation
            fetch();
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }} style={{ padding: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ width: "100%" }}>

                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }} fontWeight={800}>
                        <List /> Liste des Timesheets
                    </Typography>
                    {/* Sync and Validation Buttons */}
                    <Stack direction="row" spacing={2}>
                        {(totalCount > 0) && (
                            <Button
                                variant="outlined"
                                color="warning"
                                fullWidth
                                style={{ marginBottom: 10 }}
                                onClick={handleSyncAll}
                                disabled={!isOnline || isRefreshing}
                                startIcon={isRefreshing ? <CircularProgress size={40} color="error" /> : <Warning color="error" />}
                            >
                                <b>
                                    {isRefreshing ? 'Encours de synchronisation...' : `Tout synchroniser (${pendingNewCount} Nouveau(x), ${pendingUpdatesCount} mise(s) a jour(s), ${pendingDeletesCount} suppression(s))`}
                                </b>
                            </Button>
                        )}
                    </Stack>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: "wrap", width: "100%", justifyContent: "space-between" }}>
                        {/* Date Navigation */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                            {/* week navigation */}
                            <ButtonGroup size="small">
                                <Button
                                    variant='contained'
                                    onClick={() => {
                                        const prevWeekDate = new Date(selectedDate);
                                        prevWeekDate.setDate(prevWeekDate.getDate() - 7);
                                        handleDateChange(prevWeekDate);
                                    }}
                                    disabled={!isOnline}
                                    startIcon={<ArrowBackIos fontSize="small" />}
                                >
                                    Semaine précédente
                                </Button>

                                <Button
                                    variant='contained'
                                    onClick={() => handleDateChange(new Date())}
                                    sx={{ minWidth: '90px' }}
                                    disabled={!isOnline}
                                >
                                    Cette semaine
                                </Button>

                                <Button
                                    variant='contained'
                                    onClick={() => {
                                        const nextWeekDate = new Date(selectedDate);
                                        nextWeekDate.setDate(nextWeekDate.getDate() + 7);
                                        handleDateChange(nextWeekDate);
                                    }}
                                    disabled={!isOnline}
                                    endIcon={<ArrowForwardIos fontSize="small" />}
                                >
                                    Semaine suivante
                                </Button>
                            </ButtonGroup>


                            <Tooltip title="Clickez pour selectionner une date">
                                <Chip
                                    label={`Semaine ${weekNumber} - ${format(selectedDate, 'yyyy')}`}
                                    color="primary"
                                    onClick={() => setDatePickerOpen(true)}
                                    disabled={!isOnline}
                                    sx={{ cursor: 'pointer', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }, px: 1 }}
                                    icon={<CalendarMonth fontSize="small" />}
                                />
                            </Tooltip>

                            {/* Date picker dialog */}
                            <Dialog open={datePickerOpen} onClose={() => setDatePickerOpen(false)}>
                                <DialogTitle>Séléctionnez une semaine</DialogTitle>
                                <DialogContent sx={{ p: 4 }}>
                                    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="fr">

                                        <DatePicker
                                            label={"Selectionnez une date"}
                                            format="dd/MM/yyyy"
                                            value={selectedDate ? DateTime.fromJSDate(selectedDate) : null}
                                            onChange={(newValue) => {
                                                if (newValue && DateTime.isDateTime(newValue) && newValue.isValid) {
                                                    handleDateChange(newValue.toJSDate());
                                                    setDatePickerOpen(false);
                                                } else {
                                                    handleDateChange(null);
                                                    setDatePickerOpen(false);
                                                }
                                            }}
                                            slots={{
                                                day: makeRenderWeekDay(
                                                    hoveredDate,
                                                    setHoveredDate,
                                                    (dt: DateTime) => dt.toJSDate() // convert Luxon DateTime to JS Date
                                                ),
                                            }}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    InputLabelProps: { shrink: true },
                                                    sx: { mt: 2 },
                                                },
                                            }}
                                        />

                                    </LocalizationProvider>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setDatePickerOpen(false)}>Cancel</Button>
                                </DialogActions>
                            </Dialog>
                            {/* Week Date Range */}
                            <Typography variant="body2" color="textSecondary">
                                Du <b>{format(startOfWeek(selectedDate, { locale: fr }), 'dd MMM', { locale: fr })}</b> Au
                                {" "}<b>{format(
                                    addDays(startOfWeek(selectedDate, { locale: fr }), 6),
                                    'dd MMM yyyy',
                                    { locale: fr }
                                )}</b>
                            </Typography>
                        </Box>

                        {/* View Type Toggle */}
                        <ToggleButtonGroup
                            style={{ justifySelf: "end" }}
                            value={viewType}
                            exclusive
                            onChange={(e, newValue) => {
                                if (newValue !== null) {
                                    setViewType(newValue);
                                }
                            }}
                            size="small"
                            sx={{
                                '& .MuiToggleButtonGroup-grouped': {
                                    border: 1,
                                    borderColor: 'divider',
                                    '&.Mui-selected': {
                                        backgroundColor: 'primary.main',
                                        color: 'primary.contrastText',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: 'white',
                                        }
                                    },
                                    '&:not(:first-of-type)': {
                                        borderRadius: '0 4px 4px 0',
                                        borderLeft: 1,
                                        borderLeftColor: 'divider',
                                    },
                                    '&:first-of-type': {
                                        borderRadius: '4px 0 0 4px',
                                    },
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                    transition: theme => theme.transitions.create(['background-color', 'color', 'box-shadow']),
                                    px: 1.5,
                                    py: 1,
                                },
                                boxShadow: 1,
                                borderRadius: 1,
                                overflow: 'hidden',
                                order: { xs: 2, md: 3 }
                            }}
                        >
                            <ToggleButton value="card" aria-label="card view">
                                <Tooltip title="Cartes">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <GridView fontSize="small" />
                                        <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>
                                            Cartes
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="list" aria-label="list view">
                                <Tooltip title="Liste">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <ViewList fontSize="small" />
                                        <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>
                                            Liste
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Responsive tabs with different behavior on mobile vs desktop */}
            <Paper sx={{ width: '100%', mb: 3 }} style={{ border: "none", boxShadow: "none" }}>
                <Box sx={{ width: '100%', mb: 3 }} style={{
                    boxShadow: "none",
                }}>
                    <Paper style={{ border: "none", boxShadow: "0px 3px 3px -2px rgba(0,0,0,0.05),0px 3px 4px 0px rgba(0,0,0,0.05),0px 1px 8px 0px rgba(0,0,0,0.05)", padding: 0, borderRadius: 15 }} elevation={3}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%'
                        }}
                        >
                            {/* Mobile view: Select dropdown for smaller screens */}
                            <Box sx={{
                                display: { xs: 'block', md: 'none' },
                                p: 2
                            }} >
                                <TextField
                                    select
                                    fullWidth
                                    label="Filtre"
                                    value={tabValue}
                                    onChange={(e) => handleTabChange(e, e.target.value as TabValue)}
                                    SelectProps={{
                                        renderValue: (value) => {
                                            const labels: Record<TabValue, string> = {
                                                'all': 'Tout',
                                                'pending-sync': 'A Synchroniser',
                                                'validated': 'Validée(s)',
                                                'not-validated': 'Non validée(s)'
                                            };
                                            return labels[value as TabValue];
                                        }
                                    }}
                                >
                                    <MenuItem value="all">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <FilterListIcon sx={{ mr: 1 }} />
                                                <Typography>Tout</Typography>
                                            </Box>
                                            <Badge badgeContent={allTimesheets.length} color="primary" />
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="pending-sync" disabled={totalCount === 0}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CloudQueueIcon sx={{ mr: 1 }} />
                                                <Typography>A Synchroniser</Typography>
                                            </Box>
                                            <Badge badgeContent={totalCount} color="warning" />
                                        </Box>
                                    </MenuItem>

                                    <MenuItem value="validated" disabled={validatedCount === 0}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Check sx={{ mr: 1 }} />
                                                <Typography>Validée(s)</Typography>
                                            </Box>
                                            <Badge badgeContent={validatedCount} color="success" />
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="not-validated" disabled={notValidatedCount === 0}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Schedule sx={{ mr: 1 }} />
                                                <Typography>Non Validée(s)</Typography>
                                            </Box>
                                            <Badge badgeContent={notValidatedCount} color="error" />
                                        </Box>
                                    </MenuItem>
                                </TextField>
                            </Box>

                            {/* Desktop view: Regular tabs for larger screens */}
                            <Box
                                sx={{ display: { xs: 'none', md: 'block' }, p: 0 }}
                                style={{ border: "none", boxShadow: "none", padding: 0, borderRadius: '16px', }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        backgroundColor: 'background.paper',
                                        '& .MuiTabs-indicator': {
                                            height: '100%',
                                            borderRadius: '16px',
                                            zIndex: 1,
                                            backgroundColor: 'action.hover',
                                        },
                                        p: 0
                                    }}
                                >
                                    <Tabs
                                        value={tabValue}
                                        onChange={handleTabChange}
                                        indicatorColor="primary"
                                        textColor="primary"
                                        variant="fullWidth"
                                        sx={{
                                            '& .MuiTab-root': {
                                                zIndex: 2,
                                                transition: 'color 0.3s ease',
                                                borderRadius: '12px',
                                                fontWeight: 500,
                                                my: 0.5,
                                                '&.Mui-selected': {
                                                    color: 'white',
                                                },
                                            },
                                            '& .MuiTabs-indicator': {
                                                transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                                                borderRadius: '12px',
                                                height: '100%',
                                                top: '0%',
                                                boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
                                                backgroundColor: 'primary.main',
                                                color: "white"
                                            }
                                        }}
                                    >
                                        <Tab
                                            style={{ margin: 0 }}
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                    <FilterListIcon sx={{ mr: 0.5 }} />
                                                    <Typography sx={{ display: 'flex', alignItems: 'center' }} component="div">
                                                        Tout
                                                        <Badge
                                                            badgeContent={allTimesheets.length}
                                                            color="primary"
                                                            style={{ marginLeft: 20 }}
                                                        />
                                                    </Typography>
                                                </Box>
                                            }
                                            value="all"
                                        />
                                        <Tab
                                            style={{ margin: 0 }}
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                    <CloudQueueIcon sx={{ mr: 0.5 }} />
                                                    <Typography sx={{ display: 'flex', alignItems: 'center' }} component={"div"}>
                                                        A Syncrhoniser
                                                        <Badge
                                                            badgeContent={totalCount}
                                                            color="warning"
                                                            style={{ marginLeft: 20 }}
                                                        />
                                                    </Typography>
                                                </Box>
                                            }
                                            value="pending-sync"
                                            disabled={(totalCount === 0)}
                                        />

                                        <Tab
                                            style={{ margin: 0 }}
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                    <Check sx={{ mr: 0.5 }} />
                                                    <Typography sx={{ display: 'flex', alignItems: 'center' }} component={"div"}>
                                                        Validée(s)
                                                        <Badge
                                                            badgeContent={validatedCount}
                                                            color="success"
                                                            style={{ marginLeft: 20 }}
                                                        />
                                                    </Typography>
                                                </Box>
                                            }
                                            value="validated"
                                            disabled={validatedCount === 0}
                                        />
                                        <Tab
                                            style={{ margin: 0 }}
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                    <Schedule sx={{ mr: 0.5 }} />
                                                    <Typography sx={{ display: 'flex', alignItems: 'center' }} component={"div"}>
                                                        Non Validée(s)
                                                        <Badge
                                                            badgeContent={notValidatedCount}
                                                            color="error"
                                                            style={{ marginLeft: 20 }}
                                                        />
                                                    </Typography>
                                                </Box>
                                            }
                                            value="not-validated"
                                            disabled={notValidatedCount === 0}
                                        />
                                    </Tabs>
                                </Paper>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
                {/* Search Field */}
                <TextField
                    placeholder={userRole && ['HR'.toLowerCase(), 'Admin'.toLowerCase(), 'Foreman'.toLowerCase()].includes(userRole.toLowerCase())
                        ? "Chercher par chantier, employé, conducteur de travaux ou chef d'équipes"
                        : "Chercher par chantier ou par employé"}
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                        endAdornment: searchTerm && (
                            <IconButton
                                size="small"
                                onClick={() => setSearchTerm('')}
                                edge="end"
                            >
                                <Clear fontSize="small" />
                            </IconButton>
                        ),
                        sx: {
                            borderRadius: '15px',
                            '& fieldset': {
                                borderRadius: '15px'
                            }
                        }
                    }}
                    sx={{
                        flexGrow: 1,
                        maxWidth: { xs: '100%', sm: 400 },
                        my: { xs: 1, sm: 0 },
                        order: { xs: 3, md: 2 },
                        width: { xs: '100%', md: '100%' },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '15px'
                        }
                    }}
                />
            </Box>

            {(loading || dataLoading) && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>}
            {!(loading || dataLoading) && <>

                {tabValue === 'not-validated' && userRole && ['foreman', 'hr'].includes(userRole.toLocaleLowerCase()) && (
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectAllChecked}
                                    disabled={!isOnline}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    indeterminate={selectedTimesheets.length > 0 && selectedTimesheets.length < filteredTimesheets.filter(t => {
                                        // Use correct validation field based on user role
                                        if (userRole?.toLowerCase() === 'hr') {
                                            return !t.hr_validated && t.id;
                                        } else {
                                            return !t.foreman_validated && t.id;
                                        }
                                    }).length}
                                />
                            }
                            disabled={!isOnline}
                            label="Tout séléctionner"
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                                {selectedTimesheets.length > 0
                                    ? `${selectedTimesheets.length} timesheets selectionnée(s)`
                                    : 'Aucune timesheet séléctionneé'}
                            </Typography>

                            <Button
                                variant="contained"
                                color="success"
                                startIcon={isValidatingAll ? <CircularProgress size={20} color="inherit" /> : <Check />}
                                onClick={handleValidateSelectedTimesheets}
                                disabled={
                                    !isOnline ||
                                    isValidatingAll ||
                                    (selectedTimesheets.length === 0 && notValidatedCount === 0)
                                }
                            >
                                {isValidatingAll ? 'Validation en cours...' : selectedTimesheets.length > 0
                                    ? `Valider les timesheets séléctionnés (${selectedTimesheets.length})`
                                    : `Tout Valider (${notValidatedCount})`}
                            </Button>
                        </Box>
                    </Box>
                )}
                <Grid container spacing={3}>
                    {filteredTimesheets.length === 0 ? (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body1">
                                    {searchTerm
                                        ? `Pas de timesheet trouvée correspondante a : "${searchTerm}"`
                                        : "Pas de timesheet pour la date selectionée"
                                    }
                                </Typography>
                                {searchTerm && (
                                    <Button
                                        variant="text"
                                        color="primary"
                                        onClick={() => setSearchTerm('')}
                                        sx={{ mt: 2 }}
                                    >
                                        Effacer
                                    </Button>
                                )}
                            </Paper>
                        </Grid>
                    ) : viewType === 'card' ? (
                        // Card view with selection checkboxes
                        filteredTimesheets.map((timesheet) => (
                            <Grid item xs={12} sm={6} md={4} key={timesheet.id || timesheet.localId}>
                                <TimesheetCard
                                    masterData={data}
                                    timesheet={timesheet}
                                    onEdit={handleOpenEditDialog}
                                    onValidateForeman={handleValidateByForeman}
                                    onValidateHR={handleValidateByHR}
                                    onDelete={handleDeleteTimesheet}
                                    userRole={userRole}
                                    isOnline={isOnline}
                                    isSelected={selectedTimesheets.includes(timesheet.id as string)}
                                    showCheckbox={Boolean(
                                        tabValue === 'not-validated' &&
                                        Boolean(timesheet.id) &&
                                        (userRole && ['foreman', 'hr'].includes(userRole.toLocaleLowerCase())) &&
                                        (userRole?.toLowerCase() === 'hr'
                                            ? !timesheet.hr_validated
                                            : !timesheet.foreman_validated)
                                    )} onSelect={handleSelectTimesheet}
                                />
                            </Grid>
                        ))
                    ) : (
                        // List view with selection checkboxes
                        <Grid item xs={12}>
                            {filteredTimesheets.map((timesheet) => (
                                <TimesheetListItem
                                    masterData={data}
                                    key={timesheet.id || timesheet.localId}
                                    timesheet={timesheet}
                                    onEdit={handleOpenEditDialog}
                                    onValidateForeman={handleValidateByForeman}
                                    onValidateHR={handleValidateByHR}
                                    onDelete={handleDeleteTimesheet}
                                    userRole={userRole}
                                    isOnline={isOnline}
                                    showCheckbox={Boolean(
                                        tabValue === 'not-validated' &&
                                        Boolean(timesheet.id) &&
                                        (userRole && ['foreman', 'hr'].includes(userRole.toLocaleLowerCase())) &&
                                        (userRole?.toLowerCase() === 'hr'
                                            ? !timesheet.hr_validated
                                            : !timesheet.foreman_validated)
                                    )} isSelected={selectedTimesheets.includes(timesheet.id as string)}
                                    onSelect={handleSelectTimesheet}
                                />
                            ))}
                        </Grid>
                    )}
                </Grid>
            </>}


            {/* Edit Dialog */}
            <TimesheetEditDialog
                open={dialogOpen}
                timesheet={editTimesheet}
                onClose={handleCloseEditDialog}
                onSave={handleSaveTimesheet}
                masterData={data}
                userRole={userRole}
                isRefreshing={isRefreshing}
            />
        </Container>
    );
};

export default TimesheetList;
